import { Component } from '@angular/core';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { OperationError } from '../../../models/server-result/operation-error';
import { ValueDetail } from '../../../models/server-result/value-detail';
import { TranslateService } from '../../../services/translate/translate.service';

@Component({
	selector: 'app-toaster-showcase',
	templateUrl: './toaster-showcase.component.html',
	styleUrls: ['./toaster-showcase.component.scss'],
	standalone: false,
})
export class ToasterShowcaseComponent {
	constructor(
		private toasterService: ToasterService,
		private translateService: TranslateService
	) {}

	showSuccess(): void {
		this.toasterService.showSuccess('Operation completed successfully!');
	}

	showSuccessWithDescription(): void {
		this.toasterService.showSuccess('Data saved successfully!', {
			description: 'Your changes have been saved to the database.',
		});
	}

	showInfo(): void {
		this.toasterService.showInfo('New update available');
	}

	showInfoWithTitleAndActions(): void {
		this.toasterService.showInfo('Version 2.55.0 is now live with enhanced security features and performance improvements', {
			title: 'New Features Available',
			description:
				"Discover what's new: improved data encryption, faster report generation, and streamlined workflows. All changes are backward compatible.",
			autoClose: false,
			actions: [
				{
					label: 'View release notes',
					action: () => {
						console.log('View release notes clicked');
					},
					icon: 'fa-solid fa-sparkles',
				},
			],
		});
	}

	showWarning(): void {
		this.toasterService.showWarning('Please review your changes before submitting');
	}

	showWarningWithActions(): void {
		this.toasterService.showWarning('Unsaved changes detected', {
			description: 'You have unsaved changes. What would you like to do?',
			autoClose: false,
			actions: [
				{
					label: 'Save',
					action: () => {
						console.log('Save clicked');
						this.toasterService.showSuccess('Changes saved!');
					},
				},
				{
					label: 'Discard',
					action: () => {
						console.log('Discard clicked');
					},
					color: 'neutral',
					variant: 'outlined',
				},
			],
		});
	}

	showError(): void {
		this.toasterService.showError(this.translateService.translate('toaster-showcase.failed-to-save-changes', 'client'));
	}

	showErrorWithDescription(): void {
		this.toasterService.showError(this.translateService.translate('toaster-showcase.connection-error', 'client'), {
			description: 'Unable to connect to the server. Please check your internet connection and try again.',
		});
	}

	showErrorWithActions(): void {
		this.toasterService.showError(this.translateService.translate('toaster-showcase.failed-to-process-request', 'client'), {
			title: 'Error',
			description: 'An error occurred while processing your request.',
			autoClose: false,
			actions: [
				{
					label: 'Retry',
					action: () => {
						console.log('Retry clicked');
						this.toasterService.showInfo('Retrying...');
					},
				},
				{
					label: 'Cancel',
					action: () => {
						console.log('Cancel clicked');
					},
					color: 'neutral',
					variant: 'outlined',
				},
			],
		});
	}

	showManualClose(): void {
		this.toasterService.show({
			title: 'Important Notice',
			message: 'This message requires manual dismissal',
			description: 'Click the close button or an action button to dismiss this notification.',
			type: 'info',
			autoClose: false,
			actions: [
				{
					label: 'Got it',
					action: () => {
						console.log('Got it clicked');
					},
				},
			],
		});
	}

	showCustomDuration(): void {
		this.toasterService.show({
			message: 'This message will stay for 10 seconds',
			type: 'info',
			duration: 10000,
		});
	}

	showMultipleToasters(): void {
		this.showSuccess();
		setTimeout(() => {
			this.showInfoWithTitleAndActions();
		}, 500);
		setTimeout(() => {
			this.showWarning();
		}, 1000);
		setTimeout(() => {
			this.showErrorWithActions();
		}, 1500);
	}

	showErrorWithSingleError(): void {
		const error = new OperationError();
		error.Code = 'SUPPLIER_CONTRACT_NOT_FOUND';
		error.Field = 'ContractId';

		this.toasterService.showError(this.translateService.translate('toaster-showcase.operation-failed', 'client'), {
			title: 'Error Details',
			description: 'Please review the errors below for more information.',
			errors: [error],
			autoClose: false,
		});
	}

	showErrorWithMultipleErrors(): void {
		const error1 = new OperationError();
		error1.Code = 'VALIDATION_FAILED';
		error1.Field = 'Email';
		error1.Values = ['invalid-email@'];

		const error2 = new OperationError();
		error2.Code = 'REQUIRED_FIELD_MISSING';
		error2.Field = 'PhoneNumber';

		const error3 = new OperationError();
		error3.Code = 'INVALID_FORMAT';
		error3.Field = 'DateOfBirth';
		error3.Values = ['2025-13-45'];

		this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
			title: 'Validation Errors',
			description: 'Please correct the following errors before proceeding.',
			errors: [error1, error2, error3],
			autoClose: false,
			collapsibleErrorValues: true,
		});
	}

	showErrorWithDetails(): void {
		const error = new OperationError();
		error.Code = 'SUPPLIER_CONTRACT_NOT_FOUND';

		const detail1 = new ValueDetail();
		detail1.Code = 'ProductType';
		detail1.Value = 'CGD.CONFIRMING.EIF';

		const detail2 = new ValueDetail();
		detail2.Code = 'CredOperId';
		detail2.Value = '61459518';

		const detail3 = new ValueDetail();
		detail3.Code = 'ContractId';
		detail3.Value = '64b9aa4c-0678-47c2-a666-6be6e88769c2';

		const detail4 = new ValueDetail();
		detail4.Code = 'SupplierId';
		detail4.Value = 'b063659c-3d5e-4bbb-9602-57450cc9c238';

		error.Details = [detail1, detail2, detail3, detail4];

		this.toasterService.showError(this.translateService.translate('toaster-showcase.operation-failed', 'client'), {
			title: 'Error Details',
			description: 'Please review the errors below for more information.',
			errors: [error],
			autoClose: false,
		});
	}

	showErrorWithValues(): void {
		const error = new OperationError();
		error.Code = 'SUPPLIER_CONTRACT_NOT_FOUND';
		error.Field = 'CompanyName';
		error.Values = [
			'ProductType: CGD.CONFIRMING.EIF',
			'CredOperId: 61459518',
			'ContractId: 64b9aa4c-0678-47c2-a666-6be6e88769c2',
			'SupplierId: b063659c-3d5e-4bbb-9602-57450cc9c238',
		];

		this.toasterService.showError(this.translateService.translate('toaster-showcase.duplicate-entry-detected', 'client'), {
			title: 'Conflict Error',
			description: 'The following similar entries already exist:',
			errors: [error],
			autoClose: false,
			collapsibleErrorValues: true,
		});
	}

	showErrorComplex(): void {
		const error1 = new OperationError();
		error1.Code = 'INVALID_CONTRACT_STATE';
		error1.Field = 'ContractStatus';

		const detail1 = new ValueDetail();
		detail1.Code = 'CurrentState';
		detail1.Value = 'Draft';

		const detail2 = new ValueDetail();
		detail2.Code = 'RequiredState';
		detail2.Value = 'Active';

		error1.Details = [detail1, detail2];
		error1.Values = ['Cannot perform this operation on Draft contracts'];

		const error2 = new OperationError();
		error2.Code = 'PERMISSION_DENIED';
		error2.Field = 'UserRole';

		const detail3 = new ValueDetail();
		detail3.Code = 'RequiredRole';
		detail3.Value = 'SystemAdmin';

		const detail4 = new ValueDetail();
		detail4.Code = 'CurrentRole';
		detail4.Value = 'Supplier';

		error2.Details = [detail3, detail4];

		this.toasterService.showError(this.translateService.translate('toaster-showcase.operation-cannot-be-completed', 'client'), {
			title: 'Multiple Errors',
			description: 'Several issues were encountered while processing your request.',
			errors: [error1, error2],
			autoClose: false,
			collapsibleErrorValues: true,
			actions: [
				{
					label: 'View Details',
					action: () => {
						console.log('View details clicked');
					},
					color: 'primary',
				},
				{
					label: 'Dismiss',
					action: () => {
						console.log('Dismiss clicked');
					},
					color: 'neutral',
					variant: 'outlined',
				},
			],
		});
	}
}
